import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { Detail as DetailComponent, Confirm, useDrawer, SplitButton } from 'src/mtska-frontend-app-component';
import { useDataProvider } from 'src/mtska-frontend-data-provider';
import { useTranslation } from 'react-i18next';
import { AppContext } from 'src/mtska-frontend-app-scaffolding';
import { useLoadOne } from "src/mtska-frontend-data-provider/hooks/useLoadOne.hooks";
import JSONPretty from 'react-json-pretty';
import 'react-json-pretty/themes/monikai.css';
import { useSelector } from "react-redux";

const dataProviderName = 'zoho/manageableorder';

/** `incoming` wins on explicit keys; keeps branches only on `base`. Null on `incoming` does not wipe a rich object on `base`. */
const deepMergePreferIncoming = (base, incoming) => {
    if (incoming === undefined) return base;
    if (incoming === null) {
        return base != null && typeof base === 'object' && !Array.isArray(base) ? base : incoming;
    }
    if (typeof incoming !== 'object' || Array.isArray(incoming)) {
        return incoming;
    }
    if (base == null || typeof base !== 'object' || Array.isArray(base)) {
        return incoming;
    }
    const out = { ...base };
    for (const k of Object.keys(incoming)) {
        const b = base[k];
        const inc = incoming[k];
        if (inc !== null && typeof inc === 'object' && !Array.isArray(inc) && b != null && typeof b === 'object' && !Array.isArray(b)) {
            out[k] = deepMergePreferIncoming(b, inc);
        } else if (inc === null && b != null && typeof b === 'object' && !Array.isArray(b)) {
            out[k] = b;
        } else {
            out[k] = inc;
        }
    }
    return out;
};

const Detail = (params) => {
    const { t } = useTranslation();
    const { userCapabilities, existingModules } = useContext(AppContext);

    const { activate, close: closeDrawer } = useDrawer();

    // DATA PROVIDER
    const { item, save, doAction, loadOne, destroy, errorMessage, errors, saving, success, reloadCounter } = useDataProvider(dataProviderName, params.multipart);

    const { modelName, detailConfig } = existingModules[dataProviderName]
    const [localDetailConfig, setLocalDetailConfig] = useState();

    const detailConfigOverride = useSelector((state) => {
        const recordId = params?.id;
        if (!recordId) return undefined;
        return state?.[dataProviderName]?.global?.detailConfigOverrideById?.[recordId];
    });

    const finalDataBaselineRef = useRef(null);
    useEffect(() => {
        finalDataBaselineRef.current = null;
    }, [params?.id]);

    useEffect(() => {
        const fd = item?.data?.finalData;
        if (!fd || typeof fd !== 'object' || Array.isArray(fd)) return;
        finalDataBaselineRef.current = deepMergePreferIncoming(finalDataBaselineRef.current || {}, fd);
    }, [item?.data?.finalData]);

    const detailValues = useMemo(() => {
        const raw = item?.data;
        if (!raw || typeof raw !== 'object') return {};
        const fd = raw.finalData;
        if (fd == null || typeof fd !== 'object' || Array.isArray(fd)) {
            return { ...raw };
        }
        const mergedFd = deepMergePreferIncoming(finalDataBaselineRef.current || {}, fd);
        return { ...raw, finalData: mergedFd };
    }, [item?.data]);

    useEffect(() => {
        if (params.similar_id) {
            loadOne(params.id, { similar_id: params.similar_id });
        } else {
            loadOne(params.id);
        }
    }, [reloadCounter])

    useEffect(() => {
        const baseConfig = detailConfigOverride ?? detailConfig;
        if (!baseConfig) return;

        let cloned = baseConfig;
        try {
            cloned = JSON.parse(JSON.stringify(baseConfig));
        } catch (e) {
            // if cloning fails, fall back to mutating the baseConfig (best-effort)
        }
        const dc = populateActions(cloned);
        setLocalDetailConfig(dc);
    }, [detailConfigOverride, detailConfig, item?.data?.synchronizer?.actions?.actions])

    const [editing, setEditing] = useState(false);
    const [localError, setLocalError] = useState();

    const handleSave = (data) => {
        //console.info('SAVE', data, params)
        //save({ ...data, filters: params.filters, canReloadCounter: false }).then((data) => params.handleClose(params.uid, data.item.data)).catch(() => console.error('catch handleSave'));
    }

    const [confirmDelete, setConfirmDelete] = useState();
    const handleDelete = (id) => {
        setConfirmDelete(
            <Confirm
                oklabel={t("Delete")}
                okcallable={() => {
                    destroy && destroy(id);
                    closeDrawer();
                }}
                kolabel={t("Cancel")}
                kocallable={() => { setConfirmDelete(null) }}
                title={t("Item elimination")}>
                {t("Are you sure to remove this element?")}
            </Confirm>
        );
    }

    // Negli step in avanti salvo il modello, scatenando la validazione server side
    // Allo step finale, scateno la sincronizzazione
    // negli step indietro non faccio nulla
    const onProcessStepAction = (currentStep, stepTotal, actionInvoked, getValues) => {
        //console.info('onProcessStepAction currentStep:', currentStep, 'stepTotal:', stepTotal, 'actionInvoked:', actionInvoked, getValues());
        setLocalError();
        const values = getValues();

        if (actionInvoked > 0 && (currentStep + 1) < stepTotal) {
            const data = getValues();
            return save(data)
        } else if (actionInvoked > 0 && (currentStep + 1) == stepTotal && values['submit_actions'] == undefined) {
            setLocalError("Seleziona un'azione da compiere");
        } else if (actionInvoked > 0 && (currentStep + 1) == stepTotal && values['submit_actions'] != undefined) {
            const payload = {
                "action": "applyAction",
                "model": dataProviderName,
                "model_ids": [values['_id']],
                "data": {
                    "action": values['submit_actions'],
                    "similar_id": params.similar_id,
                    "model_ids": [values['_id']]
                }
            }

            if (values['submit_actions'] == 'reajustment' && values?.contract?.contractReajustmentStart_at) {
                payload.data.contractReajustmentStart_at = values.contract.contractReajustmentStart_at;
            }

            if (values['submit_actions'] == 'reajustment-historical' && values?.contract?.contractReajustmentEnd_at) {
                payload.data.contractReajustmentEnd_at = values.contract.contractReajustmentEnd_at;
            }

            if (values['submit_actions'] === 'manual') {
                payload.data.finalData = values.finalData;
            }

            doAction(payload)
                .then(() => {
                    if (params.afterSave) {
                        params.afterSave();
                    }
                    params.handleClose(params.uid);
                })
                .catch((e) => console.error('catch handleSave', e));
        } else {
            return true;
        }
    }

    const populateActions = (target) => {
        const availableActions = item?.data?.synchronizer?.actions?.actions?.split(',') ?? [];
        const targetField = target.steps[2].columns[0].tabs[0].fieldsets[0].fields['submit_actions'];
        targetField['options'] = availableActions.map(itm => { return { label: itm, value: itm } })

        return target;
    }

    // nei primi step, verifico se posso passare allo step successivo
    const onValidateStepAction = (currentStep, getValues, setValue, setError, resultValue) => {
        if (currentStep == 0) {
            // a prescindere dal risultato passo al successivo stage dove ho la correzione
            return true;
        } else if (currentStep == 1 && resultValue.type == 'SAVE_FAILURE') {
            return false;
        } else if (currentStep == 1) {
            // popolo la lista delle azioni disponibili per l'ordine
            let nextConfig = localDetailConfig;
            try {
                nextConfig = JSON.parse(JSON.stringify(localDetailConfig));
            } catch (e) { /* best-effort */ }
            setLocalDetailConfig(populateActions(nextConfig));
            return true;
        } else {
            return true;
        }
    }

    const onChangeFieldValueAction = (field, value, setFormValues, stepActive, setValue, getValues, dirtyPathsUnderKey = []) => {
        const dryRunForSubmitAction = (action, additionalparams = {}) => {
            loadOne(params.id, { action, similar_id: params.similar_id, ...additionalparams }).then((result) => {
                const payload = result?.item?.data ?? result?.data;
                if (payload) {
                    setFormValues((prev) => {
                        const next = { ...payload, submit_actions: action };
                        if (prev?.finalData && next?.finalData) {
                            next.finalData = deepMergePreferIncoming(prev.finalData, next.finalData);
                        }
                        return next;
                    });
                }
            });
        };
        if (field === 'submit_actions') {
            dryRunForSubmitAction(value);
        } else if (field == 'finalData' && stepActive == 2) {
            // Dopo un dry-run, RHF spesso marca molti `finalData.*` dirty in batch (idratazione): non va
            // forzato "manual" né lanciato un secondo dry-run, altrimenti la tendina passa a Manuale e i campi si svuotano.
            const dirtyCount = Array.isArray(dirtyPathsUnderKey) ? dirtyPathsUnderKey.filter(Boolean).length : 0;
            if (dirtyCount === 1) {
                setValue('submit_actions', 'manual');
            }
        } else if (field === 'contract' && stepActive === 2) {
            if (dirtyPathsUnderKey.includes('contract.contractReajustmentEnd_at')) {
                const contractReajustmentEnd_at = getValues('contract.contractReajustmentEnd_at');
                const contractReajustmentEnd_at_atom = contractReajustmentEnd_at ? new Date(contractReajustmentEnd_at).toISOString() : contractReajustmentEnd_at;
                dryRunForSubmitAction('reajustment-historical', {contractReajustmentEnd_at: contractReajustmentEnd_at_atom});
            } else if (dirtyPathsUnderKey.includes('contract.contractReajustmentStart_at')) {
                const contractReajustmentStart_at = getValues('contract.contractReajustmentStart_at');
                const contractReajustmentStart_at_atom = contractReajustmentStart_at ? new Date(contractReajustmentStart_at).toISOString() : contractReajustmentStart_at;
                dryRunForSubmitAction('reajustment', { contractReajustmentStart_at: contractReajustmentStart_at_atom });
            }
        }
    }

    return (
        <>
            {
                localDetailConfig &&
                <>
                    <DetailComponent
                        values={detailValues}
                        modelName={modelName}
                        detailConfig={localDetailConfig}
                        dataProviderName={dataProviderName}
                        disableDetailConfigOverride={true}
                        onEditing={setEditing}
                        save={handleSave}
                        saving={saving}
                        errors={errors}
                        errorMessage={errorMessage}
                        alertMessage={localError}
                        canSave={userCapabilities && userCapabilities[dataProviderName + '.edit']}
                        title={t('Zoho Order Management')}
                        subtitle={item?.data?.orderIdentifier ? t('Order Identifier') + ' ' + item?.data?.orderIdentifier : ''}
                        rightButtons={[
                            <SplitButton
                                actions={[
                                    {
                                        disabled: userCapabilities && !userCapabilities[dataProviderName + '.delete'],
                                        icon: "faTrash",
                                        label: t('Delete'),
                                        onClick: () => { handleDelete(item.data.id); }
                                    },
                            {
                                disabled: userCapabilities && !userCapabilities[dataProviderName + '.log'],
                                icon: "faClipboardList",
                                label: t('Log'),
                                onClick: () => {
                                    activate('wizards/log', {
                                        model: dataProviderName,
                                        entityId: item.data.id,
                                        detailStack: params.detailStack ?? {},
                                        title: t("Item elimination")
                                    });
                                }
                            },

                                ]}
                            />
                        ]}
                        onProcessStepAction={onProcessStepAction}
                        onValidateStepAction={onValidateStepAction}
                        onChangeFieldValueAction={onChangeFieldValueAction}
                        {...params}
                    />
                </>
            }


            {confirmDelete}
        </>
    )
}

export default Detail;


